import { useState, useCallback, useEffect, useMemo } from 'react';
import useAxios from '../../../../hooks/useAxios';
import Constants from '../../../../Constants';
import { getDateQueryParams } from '../../../../utils/helper';
import { COMPANY_STATUS } from '@demandproai/shared';

const useCompanyStatus = (selectedRange, statuses = []) => {
    const { getData } = useAxios();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [companiesData, setCompaniesData] = useState({});

    const fetchCompanyByStatus = useCallback(
        async (selectedRange, status) => {
            const query = getDateQueryParams(selectedRange);
            query.status = status;
            const searchQuery = new URLSearchParams(query).toString();
            const url = `${Constants.ApiUrl.super.dashboard.companyStatus}?${searchQuery}`;

            return getData(url);
        },
        [getData]
    );

    const fetchStatusCompanies = useCallback(
        async (selectedRange, statuses) => {
            setLoading(true);
            setError(null);

            try {
                const promises = statuses.map(status => fetchCompanyByStatus(selectedRange, status));

                const results = await Promise.all(promises);
                const fetchedCompaniesData = {};
                results.forEach((item, index) => {
                    const status = statuses[index];
                    fetchedCompaniesData[status] = item?.companies || [];
                });

                setCompaniesData(fetchedCompaniesData);
                return fetchedCompaniesData;
            } catch (err) {
                setError(err.message || 'Failed to fetch company data');
                setCompaniesData({});
                return null;
            }finally {
                setLoading(false);
            }
        },
        [fetchCompanyByStatus]
    );

    useEffect(() => {
        if (statuses.length > 0) {
            fetchStatusCompanies(selectedRange, statuses);
        }
    }, [selectedRange, statuses, fetchStatusCompanies]);

    const companiesYTDStats = useMemo(() => {
        const subscribedCompanies = companiesData[COMPANY_STATUS.SUBSCRIBED] || [];
        const canceledCompanies = companiesData[COMPANY_STATUS.CANCELED] || [];
        const subscribedCount = subscribedCompanies.length;
        const canceledCount = canceledCompanies.length;
        const netChangeValue = subscribedCount - canceledCount;
        const netChange = netChangeValue > 0 ? `+${netChangeValue}`: `${netChangeValue}`;
        const churnRate = `${subscribedCount > 0 ? ((canceledCount / subscribedCount) * 100).toFixed(2) : 0}%`;

        return { subscribedCount, canceledCount, netChange, churnRate };
    }, [companiesData]);

    return { fetchStatusCompanies,companiesYTDStats,companiesData, loading, error };
};

export default useCompanyStatus;